import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FileText, LockKeyhole } from 'lucide-react';
import SidebarContext from './SidebarContext';
import { APP_SIDEBAR, MODULE_SUBMENUS } from '../menuConfig';
import { getSubmenuRoute } from '../utils/moduleRouteUtils';
import hospitalLogo from '../hospital-logo.png';
import SidebarUserCard from '../components/common/SidebarUserCard';
import '../styles/app-shell.css';

export const SIDEBAR_WIDTH = 240;

function isPrivilegedUser(user) {
  const role = String(user?.role || user?.roles || '').toLowerCase().replace(/[^a-z0-9]+/g, '_');
  return user?.is_superadmin === true
    || user?.isSuperAdmin === true
    || Number(user?.is_superadmin) === 1
    || Number(user?.isSuperAdmin) === 1
    || role.includes('superadmin')
    || role.includes('super_admin');
}

function normalizeModuleKey(value) {
  const key = String(value || '').toLowerCase().trim();
  if (key.includes('patholog') || key.includes('lab')) return 'pathology';
  if (key.includes('pharmac')) return 'pharmacy';
  if (key.includes('out door') || key.includes('outdoor') || key === 'opd') return 'opd';
  if (key.includes('in door') || key.includes('indoor') || key === 'ipd') return 'ipd';
  if (key.includes('patient') && key.includes('record')) return 'patient_record';
  if (key.includes('radiolog') || key.includes('imaging')) return 'radiology';
  if (key.includes('blood')) return 'blood_bank';
  if (key.includes('operation') || key.includes('theatre')) return 'operation_theatre';
  if (key.includes('icu') || key.includes('hdu')) return 'hdu_icu';
  if (key.includes('dental')) return 'dentistry';
  if (key.includes('admin') || key === 'settings' || key === 'reports' || key.includes('cash')) return 'admin';
  return key;
}

function getAllowedModules() {
  try {
    const user = JSON.parse(localStorage.getItem('hmis_user') || '{}');
    if (isPrivilegedUser(user)) return null;
    let modules = Array.isArray(user.modules) ? user.modules : [];
    if (!modules.length && user.modules_access) {
      const raw = Array.isArray(user.modules_access) ? user.modules_access : JSON.parse(user.modules_access);
      modules = Array.isArray(raw) ? raw : [];
    }
    if (!modules.length) return null;
    return new Set(modules.map((item) => normalizeModuleKey(
      typeof item === 'object' ? (item.code || item.module_code || item.module_key || item.name || item.key) : item
    )));
  } catch (e) {
    return null;
  }
}

function canAccessSidebarItem(item) {
  if (item.key === 'main' || item.key === 'dashboard') return true;
  const allowed = getAllowedModules();
  return !allowed || allowed.has(normalizeModuleKey(item.key)) || allowed.has(normalizeModuleKey(item.label));
}

// use getSubmenuRoute to resolve submenu keys to explicit routes

// Number the ordered diagnostic workflows in the sidebar.
const PATHOLOGY_WORKFLOW_STEPS = {
  lab_invoices: 1,
  lab_sample: 2,
  lab_upload: 3,
  lab_supervision: 4,
  lab_concluded: 5,
};
const RADIOLOGY_WORKFLOW_STEPS = {
  rad_orders: 1,
  rad_queue: 2,
  rad_studies: 3,
  rad_reporting: 4,
  rad_verification: 5,
  rad_completed: 6,
};

function SidebarInner({ menu }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { collapsed, setCollapsed, activeKey, setActiveKey } = useContext(SidebarContext);
  const [opdActivePanel, setOpdActivePanel] = useState(() => {
    try { return localStorage.getItem('hmis_opd_active_panel') || 'table'; } catch (e) { return 'table'; }
  });

  useEffect(() => { try { localStorage.setItem('hmis_opd_active_panel', opdActivePanel || 'table'); } catch (e) {} }, [opdActivePanel]);

  useEffect(() => {
    function onOpdPanelChange(e) {
      try { const p = e && e.detail && e.detail.panel; if (p) setOpdActivePanel(p); } catch (err) {}
    }
    window.addEventListener('hmis:opd:panel', onOpdPanelChange);
    return () => window.removeEventListener('hmis:opd:panel', onOpdPanelChange);
  }, []);

  useEffect(() => {
    const key = menu.find((m) => (m.route && location.pathname.startsWith(m.route)))?.key || null;
    if (key) setActiveKey(key);
  }, [location.pathname]);

  useEffect(() => {
    // If route changes, clear any manual suppression so modules can auto-focus again.
    setSuppressRouteFocus(false);
  }, [location.pathname]);

  const companyName = useMemo(() => {
    try {
      const profile = window.companyProfile || null;
      if (profile && (profile.name || profile.hospitalName)) {
        return String(profile.name || profile.hospitalName);
      }
    } catch (e) {}
    return 'HMIS';
  }, []);

  const moduleKey = location.pathname.replace(/^\/+/, '').split('/')[0] || '';
  const submenu = MODULE_SUBMENUS[moduleKey] || [];
  // Normalize submenu entries to a flat list while supporting an optional `children` array on items.
  const normalizedSubs = useMemo(() => {
    const out = [];
    (submenu || []).forEach((it) => {
      if (it && Array.isArray(it.children) && it.children.length) {
        out.push({ key: it.key, label: it.label });
        it.children.forEach((c) => out.push(c));
      } else {
        out.push(it);
      }
    });
    return out;
  }, [moduleKey, JSON.stringify(submenu)]);

  // Build a parent->children map from the original submenu definition so we can render
  // direct `children` entries at the same indentation (even if their keys contain extra underscores).
  const parentToChildren = useMemo(() => {
    const m = {};
    (submenu || []).forEach((it) => {
      if (it && Array.isArray(it.children) && it.children.length) {
        m[it.key] = it.children.map(c => c.key);
      }
    });
    return m;
  }, [JSON.stringify(submenu)]);
  const [sidebarFocus, setSidebarFocus] = useState(null);
  const [pinnedEnd, setPinnedEnd] = useState(false);
  const [suppressRouteFocus, setSuppressRouteFocus] = useState(false);
  const [expandedParents, setExpandedParents] = useState({});

  useEffect(() => {
    // keep parent submenus collapsed by default; user can toggle
  }, [location.pathname]);

  // If the user clicked to focus a module (sidebarFocus) or the current route is a module
  // with a submenu, treat that module as the 'activeModule' and only render its button + submenu.
  const activeModuleKey = sidebarFocus ? sidebarFocus.key : ((normalizedSubs.length && !suppressRouteFocus) ? moduleKey : null);
  const activeModuleLabel = sidebarFocus ? sidebarFocus.label : ((normalizedSubs.length && !suppressRouteFocus) ? (menu.find(m => m.key === moduleKey)?.label || moduleKey.toUpperCase()) : null);
  const activeModuleIcon = sidebarFocus ? sidebarFocus.icon : ((submenu.length && !suppressRouteFocus) ? menu.find(m => m.key === moduleKey)?.icon : null);

  const iconTone = (label) => {
    switch (label) {
      case 'Dashboard': return { color: '#3b82f6', bg: 'rgba(59,130,246,0.14)' };
      case 'Outdoor Patient Department': return { color: '#22d3ee', bg: 'rgba(34,211,238,0.16)' };
      case 'Emergency': return { color: '#ef4444', bg: 'rgba(239,68,68,0.16)' };
      case 'Indoor Patient Department': return { color: '#60a5fa', bg: 'rgba(96,165,250,0.14)' };
      case 'Patient Records': return { color: '#cbd5e1', bg: 'rgba(203,213,225,0.16)' };
      case 'Pharmacy': return { color: '#4ade80', bg: 'rgba(74,222,128,0.16)' };
      case 'Laboratory': return { color: '#a78bfa', bg: 'rgba(167,139,250,0.17)' };
      case 'Radiology': return { color: '#f59e0b', bg: 'rgba(245,158,11,0.16)' };
      case 'Dentistry': return { color: '#ffffff', bg: 'rgba(255,255,255,0.18)' };
      case 'Blood Bank': return { color: '#ff2d2d', bg: 'rgba(255,45,45,0.18)' };
      case 'Operation Theatre': return { color: '#2dd4bf', bg: 'rgba(45,212,191,0.16)' };
      case 'ICU / HDU': return { color: '#c084fc', bg: 'rgba(192,132,252,0.17)' };
      case 'Admin Section': return { color: '#94a3b8', bg: 'rgba(148,163,184,0.17)' };
      case 'Reports': return { color: '#fb923c', bg: 'rgba(251,146,60,0.16)' };
      case 'Cash Reconciliation': return { color: '#a3e635', bg: 'rgba(163,230,53,0.16)' };
      case 'HR Management': return { color: '#60a5fa', bg: 'rgba(96,165,250,0.14)' };
      case 'Settings': return { color: '#818cf8', bg: 'rgba(129,140,248,0.16)' };
      default: return { color: '#3b82f6', bg: 'rgba(59,130,246,0.14)' };
    }
  };

  return (
    <aside className="hmisx-side" aria-label="Sidebar">
      <div className="hmisx-brand">
        <div className="hmisx-brand-logo">
          <img src={hospitalLogo} alt="hospital" />
        </div>
        <div className="hmisx-brand-copy">
          <h1>{companyName}</h1>
          <p>HMIS Dashboard</p>
        </div>
      </div>

      <nav className="hmisx-nav-scroll">
        {activeModuleKey ? (
          <section className={`hmisx-nav-group hmisx-nav-group--focused ${pinnedEnd ? 'hmisx-focused-pinned' : ''}`}>
            <React.Fragment key={`focused-${activeModuleKey}-${activeModuleLabel}`}>
              <button
                type="button"
                className={`hmisx-nav-btn hmisx-nav-btn--focused`}
                onClick={() => { setSidebarFocus(null); setPinnedEnd(false); setSuppressRouteFocus(true); }}
              >
                <span className="hmisx-nav-ic" style={{ background: iconTone(activeModuleLabel).bg, color: iconTone(activeModuleLabel).color }}>
                  {activeModuleIcon ? React.createElement(activeModuleIcon, { size: 13, strokeWidth: 1.9 }) : null}
                </span>
                <span className="hmisx-nav-txt">{activeModuleLabel}</span>
              </button>

              {/* If pinnedEnd is true we want submenu to appear at the end of the nav - use flex spacer */}
              {pinnedEnd ? <div style={{ flex: 1 }} /> : null}

                <div className="hmisx-module-submenu">
                {(normalizedSubs || []).map((s) => {
                  // Determine if this submenu has a parent among the module's submenu keys
                  const subs = normalizedSubs || [];

                  // Determine parent based on explicit `children` mapping first.
                  let parentKey = null;
                  for (const [pk, kids] of Object.entries(parentToChildren || {})) {
                    if (kids.includes(s.key)) { parentKey = pk; break; }
                  }

                  const isChild = Boolean(parentKey);
                  if (isChild && !expandedParents[parentKey]) return null;

                  // When parent is defined via `children`, treat direct children as one-step indented.
                  let indentClass = '';
                  if (parentKey) {
                    indentClass = `hmisx-sub-indent-2`;
                  }

                  // An item is considered a parent if it has an explicit children array in the original submenu.
                  const thisHasChildren = Boolean((submenu || []).find(it => it && it.key === s.key && Array.isArray(it.children) && it.children.length));

                  const workflowStep = activeModuleKey === 'pathology'
                    ? PATHOLOGY_WORKFLOW_STEPS[s.key]
                    : activeModuleKey === 'radiology'
                      ? RADIOLOGY_WORKFLOW_STEPS[s.key]
                      : null;
                  if (workflowStep && !indentClass) indentClass = 'hmisx-sub-indent-2';
                  const workflowClass = activeModuleKey === 'radiology' && workflowStep
                    ? 'hmisx-radiology-workflow-step'
                    : '';

                  return (
                    <button
                      key={s.key}
                      type="button"
                      disabled={Boolean(s.disabled)}
                      className={`hmisx-nav-btn hmisx-nav-btn--sub ${indentClass} ${workflowClass}`.trim()}
                      onClick={() => {
                        if (s.disabled) return;
                        // If this submenu is a parent (has children), toggle it open/closed
                        if (thisHasChildren) {
                          setExpandedParents((p) => ({ ...p, [s.key]: !p[s.key] }));
                          return;
                        }
                        navigate(getSubmenuRoute(activeModuleKey, s.key));
                        try { window.dispatchEvent(new CustomEvent(`hmis:${activeModuleKey}:action`, { detail: { action: s.key } })); } catch (e) {}
                      }}
                    >
                      {workflowStep && <span className="hmisx-nav-step-badge">{workflowStep}</span>}
                      {parentKey === 'ipd_docs' ? <span className="hmisx-nav-ic" aria-hidden="true"><FileText size={13} strokeWidth={1.9} /></span> : null}
                      <span className="hmisx-nav-txt hmisx-nav-subtxt">{s.label}</span>
                      {s.disabled && <LockKeyhole className="hmisx-locked-mark" size={14} aria-label="Not available" />}
                      {thisHasChildren && (
                        <span className="hmisx-nav-expanded-mark">{expandedParents[s.key] ? '▾' : '▸'}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </React.Fragment>
          </section>
        ) : (
          menu.map((item) => {
            const subs = MODULE_SUBMENUS[item.key] || [];
            const enabled = canAccessSidebarItem(item);
            return (
              <React.Fragment key={`menu-${item.key}`}>
                <button
                  type="button"
                  disabled={!enabled}
                  onClick={() => {
                    if (!enabled) return;
                    // If this top-level item has module submenus, toggle focus/expand instead of navigating
                    if (subs.length) {
                        // Toggle focus (expand/collapse) for modules with submenus
                        if (sidebarFocus && sidebarFocus.key === item.key) {
                          // currently focused -> collapse
                          setSidebarFocus(null);
                          setPinnedEnd(false);
                        } else {
                          // expand
                          setSidebarFocus({ key: item.key, label: item.label, icon: item.icon });
                          setPinnedEnd(false);
                        }

                        // Navigate to the module's landing route. For Admin/HR use explicit path.
                        try {
                          const lab = String(item.label || '').toLowerCase();
                          if (lab.includes('hr')) {
                            setActiveKey(item.key);
                            navigate('/admin/hr');
                          } else if (item.route) {
                            setActiveKey(item.key);
                            navigate(item.route);
                          } else {
                            setActiveKey(item.key);
                            navigate('/' + item.key);
                          }
                        } catch (e) {}
                        return;
                      }
                    setActiveKey(item.key);
                    navigate(item.route);
                  }}
                  key={`${item.key}-${item.label}`}
                  className={`hmisx-nav-btn ${activeKey === item.key ? 'active' : ''}`}
                >
                  <span className="hmisx-nav-ic" style={{ background: iconTone(item.label).bg, color: iconTone(item.label).color }}>
                    {item.icon ? React.createElement(item.icon, { size: 13, strokeWidth: 1.9 }) : null}
                  </span>
                  <span className="hmisx-nav-txt">{item.label}</span>
                  {!enabled && <LockKeyhole className="hmisx-locked-mark" size={14} aria-label="Not allowed" />}
                </button>

                {/* Submenus are shown only when a module is focused (via the focused block above). */}
              </React.Fragment>
            );
          })
        )}

        { /* No separate module submenu block — handled by activeModuleKey rendering above */ }
      </nav>

      {activeModuleKey ? (
        <div style={{ padding: 12 }}>
          <SidebarUserCard
            user={(() => { try { return JSON.parse(localStorage.getItem('hmis_user') || '{}'); } catch (e) { return {}; } })()}
            onProfile={() => { try { window.dispatchEvent(new CustomEvent('hmis:openProfileMenu')); } catch (e) {} }}
            onChangePassword={() => { try { window.dispatchEvent(new CustomEvent('hmis:openChangePassword')); } catch (e) {} }}
            onSignout={() => {
              try { localStorage.removeItem('hmis_token'); } catch (e) {}
              try { localStorage.removeItem('hmis_user'); } catch (e) {}
              try { window.dispatchEvent(new CustomEvent('hmis:logout')); } catch (e) {}
              try { if (window && typeof window.appNavigate === 'function') { window.appNavigate('login'); return; } } catch (e) {}
              try { window.location.hash = '#/login'; } catch (e) {}
            }}
          />
        </div>
      ) : (
        <div className="hmisx-help">
          <h4>Need Help?</h4>
          <p>Contact IT Support</p>
          <button type="button">Contact Now</button>
        </div>
      )}
    </aside>
  );
}

export default function Sidebar(props) {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('hmis_sidebar_collapsed') === '1'; } catch (e) { return false; }
  });
  const [activeKey, setActiveKey] = useState(() => {
    try { return localStorage.getItem('hmis_sidebar_active') || null; } catch (e) { return null; }
  });

  useEffect(() => { try { localStorage.setItem('hmis_sidebar_collapsed', collapsed ? '1' : '0'); } catch (e) {} }, [collapsed]);
  useEffect(() => { try { if (activeKey) localStorage.setItem('hmis_sidebar_active', activeKey); } catch (e) {} }, [activeKey]);

  // Reuse the existing application sidebar structure from NewLookDashboard
  const menu = props.menu || APP_SIDEBAR.flatMap((group) => group.items.map((it) => ({
    key: it.key,
    label: it.label,
    icon: it.icon,
    route: it.route || '/' + it.key,
  })));

  return (
    <SidebarContext.Provider value={{ collapsed, setCollapsed, activeKey, setActiveKey }}>
      <SidebarInner menu={menu} />
    </SidebarContext.Provider>
  );
}
